import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ActionBar } from '@databases/action-bar/ActionBar';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import { collabStateManager } from '@databases/collab-state-manager';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { contextualMenuState } from '@databases/contextual-menu';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  useDatabaseStreamState,
  useRovoGradientAnimationForCells,
} from '@databases/database-ai-first-creation-ui';
import { useDatabaseContent } from '@databases/database-state';
import {
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
  VERT_DRAG_HANDLER_ICON_WIDTH,
} from '@databases/icons';
import useClearValuesKeyboardShortcut from '@databases/keyboard-shortcuts/useClearValuesKeyboardShortcut';
import { type ScrollPosition } from '@databases/layout/scroll-position';
import Z_INDEX from '@databases/layout/z-index';
import { type PanelsType } from '@databases/panel/PanelContext';
import usePanelState from '@databases/panel/usePanelState';
import QuickCalculationsRow from '@databases/quick-calc/QuickCalculationsRow';
import { getGridTemplateColumns } from '@databases/quick-calc/utils';
import { useDebouncedCallback } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectCurrentSelectionMode,
  selectHasSelectedCells,
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldsCount,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getService } from '@databases/service-container';
import TableResizer, { isTableElement } from '@databases/table-resizer';
import {
  LEFT_ACTIONS_MID_WIDTH,
  RIGHT_ACTIONS_MID_WIDTH,
} from '@databases/table-resizer/constants';
import { type DatabaseEntry } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  NUMBERED_COLUMN_WIDTH,
  TABLE_VIEW_HARD_COLUMN_MIN_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
} from '@databases/utils/constants';
import { getDatabaseFields } from '@databases/utils/database/utils';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import { useJiraFilterLoading } from '@databases/views/useJiraFilterLoading';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { shouldShowEntryPrefix } from '../../../utils';
import { tableRowMinHeight } from '../../styled';
import AddEntryButton from '../table-layout/AddEntryButton';

import FilterLoadingOverlay from './FilterLoadingOverlay';
import { Table, TableLayoutWrapper } from './Table';
import TableLayoutHeaderRow from './TableLayoutHeaderRow';
import TableLayoutRows from './TableLayoutRows';
import useHasNumberedEntries from './UseHasNumberedEntries';
import { useDeselectOnStreaming } from './useDeselectOnStreaming';

export const ADD_ENTRY_BUTTON_HEIGHT = 38;
export const QUICK_CALC_ROW_HEIGHT = 48;
export const STICKY_BOTTOM_ELEMENTS_HEIGHT =
  ADD_ENTRY_BUTTON_HEIGHT + QUICK_CALC_ROW_HEIGHT;

export interface TableLayoutProps {
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
  contentId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  couldEditFields: boolean;
  renderSection: ScrollPosition;
  containerWidth: number;
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  containerTop: number;
  containerLeft: number;
  handleEntry: (entry: DatabaseEntry) => void;
  isConfluencePanelOpen: boolean;
}

const determineNewFieldWidth = (width: number | undefined) => {
  if (width === undefined) {
    return;
  }
  return Math.max(width, TABLE_VIEW_HARD_COLUMN_MIN_WIDTH);
};

const calculateMaxHeight = (table: HTMLTableElement | null) => {
  if (!table) {
    return 0;
  }
  const windowHeight = globalThis.innerHeight;
  const tableTop = table.getBoundingClientRect().top;
  const calculatedMaxHeight =
    windowHeight - tableTop - ADD_ENTRY_BUTTON_HEIGHT - QUICK_CALC_ROW_HEIGHT - 5;
  return calculatedMaxHeight;
};

const addEntryAndQuickCalculationsContainerStyles = xcss({
  position: 'sticky',
  bottom: 'space.0',
  backgroundColor: 'elevation.surface',
  display: 'flex',
});

const ActionsContainerGutter = styled.div<{
  side: 'left' | 'right';
  isVisualRefreshM1Enabled: boolean;
}>`
  width: ${({ side, isVisualRefreshM1Enabled }) =>
    side === 'left' && isVisualRefreshM1Enabled
      ? LEFT_ACTIONS_MID_WIDTH
      : RIGHT_ACTIONS_MID_WIDTH}px};
  ${({ side }) => (side === 'left' ? 'left: 0px;' : 'right: 0px;')}
  position: sticky;
  z-index: 1000;
  background: ${token('elevation.surface', '#FFFFFF')};
`;

const ActionsContainer = styled.div`
  flex: 1;
`;

const addEntryButtonWrapperStyles = xcss({
  position: 'relative',
  zIndex: 'card',
});

const filterLoadingOverlayContainerStyles = xcss({
  position: 'absolute',
  top: 'space.0',
  left: 'space.0',
  height: '40px',
  zIndex: 'blanket',
});

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const TableLayout: FunctionComponent<TableLayoutProps> = ({
  editingIds,
  setEditingIds,
  contentId,
  isContentEditable,
  canEditContent,
  canEditFields,
  couldEditFields,
  renderSection,
  containerWidth,
  addEntryButtonRef,
  containerTop,
  containerLeft,
  handleEntry,
  isConfluencePanelOpen,
}) => {
  const { view } = useView();
  const { openThread } = useCommentStateContext();
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const { referencing } = useDatabaseContent();
  const isJiraSliceAndDiceEnabled = getExperimentValue(
    'databasesJiraSliceAndDice',
    'enabled',
    { fireExperimentExposure: false }
  );
  const jiraFilterLoading = useJiraFilterLoading(contentId);
  const isJiraFilterLoading = isJiraSliceAndDiceEnabled && jiraFilterLoading;

  const dragHandlerTdElementWidth = isVisualRefreshM1Enabled
    ? DRAG_HANDLER_TD_ELEMENT_WIDTH
    : VERT_DRAG_HANDLER_ICON_WIDTH;

  const [
    databaseDraftView,
    databaseDraftFieldWidths,
    hasSelection,
    selectedEntriesCount,
    selectedFieldsCount,
    selectedValuesCount,
    currentSelectionMode,
    entryId,
    collabManager,
    hasVisibleFields,
  ] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraftView = selectDatabaseDraftByContentId(state, {
      contentId,
      viewSettings: view.settings,
      includeDeleted: true,
    });
    const hasSelection = selectHasSelectedCells(state, {
      contentId,
    });
    const selectedEntries = selectSelectedEntireEntryIds(state, {
      contentId: contentId,
    });

    const entryId: string = selectedEntries[0] ?? '';
    const selectedFields = selectSelectedEntireFieldsCount(state, {
      contentId: contentId,
    });
    const selectedValuesCount = selectSelectedValuesCount(state, {
      contentId: contentId,
    });

    const currentSelectionMode = selectCurrentSelectionMode(state, {
      contentId: contentId,
    });

    const collabManager = collabStateManager.getCollabManager(contentId);

    const databaseFields = getDatabaseFields(databaseDraftView);

    return [
      databaseDraftView,
      databaseFields.map((field) => field.width),
      hasSelection,
      selectedEntries.length,
      selectedFields,
      selectedValuesCount,
      currentSelectionMode,
      entryId,
      collabManager,
      databaseFields.length > 0,
    ] as const;
  });
  const hasEntries = databaseDraftView.entries.length > 0;
  const contextualMenu = useSnapshot(contextualMenuState);
  const showEntryPrefix = shouldShowEntryPrefix(view);
  const hasNumberedEntries = useHasNumberedEntries({ contentId }) && showEntryPrefix;

  const shouldRenderActionBar = useMemo(() => {
    const isCellMenuOpen = contextualMenu.menuData?.type === 'value';
    if (!isVisualRefreshM1Enabled || isCellMenuOpen) {
      return false;
    }

    const hasValidSelection =
      selectedEntriesCount > 0 || selectedFieldsCount > 1 || selectedValuesCount > 1;

    const canShowActionBar =
      canEditContent || (!canEditContent && selectedEntriesCount === 1);

    return !referencing && canShowActionBar && hasValidSelection;
  }, [
    isVisualRefreshM1Enabled,
    contextualMenu,
    referencing,
    canEditContent,
    selectedEntriesCount,
    selectedFieldsCount,
    selectedValuesCount,
  ]);

  // Hold the field widths in a local state while resizing the columns.
  const [resizingFieldWidths, setResizingFieldWidths] = useState<
    (number | undefined)[] | undefined
  >(undefined);
  const handleResize = useCallback(
    (widths: Array<number | undefined>) =>
      setResizingFieldWidths(widths.map((w) => determineNewFieldWidth(w))),
    []
  );

  // Returns either the resizing field widths while resizing or the field widths from the store.
  const fieldWidths = useMemo(
    () => [
      hasNumberedEntries && hasVisibleFields ? NUMBERED_COLUMN_WIDTH : 0,
      ...(resizingFieldWidths?.length ? resizingFieldWidths : databaseDraftFieldWidths),
    ],
    [hasNumberedEntries, resizingFieldWidths, databaseDraftFieldWidths, hasVisibleFields]
  );

  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const store = defaultStoreUtils.useStore();
  const handleSubmitResize = useCallback(
    (newWidths: Array<number | undefined>) => {
      setResizingFieldWidths(undefined);
      batch(() => {
        collabDatabaseBatch(store, contentId, () => {
          for (let i = 0; i < newWidths.length; i++) {
            const fieldId = databaseDraftView.fields[i];
            if (newWidths[i] !== databaseDraftFieldWidths[i] && fieldId) {
              dispatch(
                addChangeToDraft(contentId, {
                  type: DatabaseChangeType.SET_FIELD_WIDTH,
                  fieldId,
                  width: determineNewFieldWidth(newWidths[i]),
                })
              );
            }
          }
        });
      });
    },
    [store, contentId, databaseDraftView, databaseDraftFieldWidths, dispatch]
  );

  const minWidth =
    fieldWidths.reduce<number>((p, w) => p + (w ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH), 0) +
    RIGHT_ACTIONS_MID_WIDTH;

  const gridTemplateColumns = useMemo(() => {
    const getDragHandlerColumn = () => {
      if (!canEditContent || !showEntryPrefix) {
        return '';
      }
      return fieldWidths.some((w) => w == null)
        ? `${dragHandlerTdElementWidth / 2}px`
        : '0fr';
    };

    return `${getDragHandlerColumn()} ${getGridTemplateColumns(
      fieldWidths
    )} ${RIGHT_ACTIONS_MID_WIDTH}px`;
  }, [canEditContent, fieldWidths, dragHandlerTdElementWidth, showEntryPrefix]);

  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);
  const tableHeaderRowRef = useRef<HTMLTableRowElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const { isStreaming } = useDatabaseStreamState();
  const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const isStreamingEnabled = isDbAiFirstCreationEnabled && isStreaming;
  const { fieldIdSelectedForProcessing, isSingleFieldStreaming } = useSnapshot(
    aiFirstCreationStreamingState
  );

  useRovoGradientAnimationForCells(
    tableRef,
    isStreamingEnabled && Boolean(isSingleFieldStreaming),
    fieldIdSelectedForProcessing
  );

  useDeselectOnStreaming(isStreaming);

  const [tableMaxHeight, setTableMaxHeight] = useState<number>(0);

  const isDatabasePresenceEnabled = getExperimentValue('databasePresence', 'enabled', {
    fireExperimentExposure: false,
  });

  const handleDeselect = useCallback(() => {
    if (collabManager && isDatabasePresenceEnabled) {
      collabManager.updateTelepointer(undefined, undefined);
    }
    selectionsActions.deselectAll();
  }, [selectionsActions, collabManager, isDatabasePresenceEnabled]);

  const excludedTargets = [
    '[id="database-contextual-menu"]',
    '[data-testid="confirm-dialog--blanket"]',
    '[id="quick-calc-menu"]',
    '[id="bulk-edit-values"]',
    '[id="database-action-bar-container"]',
  ];

  const { isPanelVisible } = usePanelState();
  const panels: Array<keyof PanelsType> = [
    'FieldSettingsPanel',
    'ViewSettingsPanel',
    'EntryViewPanel',
    'DatabaseSetupPanel',
  ];
  const isAnyPanelOpen = panels.some((panel) => isPanelVisible(panel));
  useHandleOutsideClick(
    handleDeselect,
    tableRef,
    excludedTargets,
    hasSelection && !isAnyPanelOpen
  );

  const handleBlur = useCallback(() => {
    if (
      !openThread.valueId &&
      !(document.activeElement instanceof HTMLIFrameElement) &&
      document.hasFocus()
    ) {
      selectionsActions.deselectAll();
    }
  }, [selectionsActions, openThread.valueId]);

  const updateTableMaxHeight = useDebouncedCallback(() => {
    const calculatedMaxHeight = calculateMaxHeight(tableRef.current);
    setTableMaxHeight(calculatedMaxHeight);
  }, 300);

  useEffect(() => {
    globalThis.addEventListener('blur', handleBlur);
    return () => {
      globalThis.removeEventListener('blur', handleBlur);
    };
  }, [handleBlur]);

  useEffect(() => {
    globalThis.addEventListener('resize', updateTableMaxHeight);
    return () => {
      globalThis.removeEventListener('resize', updateTableMaxHeight);
    };
  }, [updateTableMaxHeight]);

  useClearValuesKeyboardShortcut({ contentId, canEditContent });

  useEffect(() => {
    actionBarState.setActionBarState({ isActionBarEnabled: shouldRenderActionBar });
  }, [shouldRenderActionBar]);

  // `maxHeight` is initialized to 0 and only updated on a resize event. Only use the
  // minimum between table height and max height *if* a resize event has not occurred.
  const tableHeight =
    tableMaxHeight !== 0
      ? Math.min(tableRef.current?.offsetHeight ?? 0, tableMaxHeight)
      : tableRef.current?.offsetHeight ?? 0;
  const containerHeight = tableHeight + editingIds.size * tableRowMinHeight;

  const tableWidth = fieldWidths.reduce((accumulator, currentValue) => {
    if (accumulator !== undefined && currentValue !== undefined) {
      return accumulator + currentValue;
    }
  }, 0);

  const [quickCalcColumnWidths, setQuickCalcColumnWidths] = useState<
    (number | undefined)[]
  >([]);

  useEffect(() => {
    const tableHeaders =
      tableHeaderRowRef.current !== null
        ? [...tableHeaderRowRef.current.children].filter(isTableElement)
        : [];
    // The 1st item is the numbered entries column
    const tableFieldHeaders = tableHeaders.slice(1);
    const newWidths = databaseDraftFieldWidths.map(
      (width, index) => width || tableFieldHeaders[index]?.offsetWidth
    );

    // Only update if widths have actually changed, to prevent infinite re-renders
    setQuickCalcColumnWidths((prevWidths) => {
      if (
        prevWidths.length !== newWidths.length ||
        prevWidths.some((width, index) => width !== newWidths[index])
      ) {
        return newWidths;
      }
      return prevWidths;
    });
  }, [databaseDraftFieldWidths, hasVisibleFields, containerWidth]);

  return (
    <>
      {shouldRenderActionBar && (
        <ActionBar
          contentId={contentId}
          canEditContent={canEditContent}
          selectionMode={currentSelectionMode}
          entryId={entryId}
        />
      )}
      <TableLayoutWrapper containerWidth={containerWidth}>
        <TableResizer
          widths={fieldWidths}
          onResizeSubmit={handleSubmitResize}
          onResize={handleResize}
          minWidth={TABLE_VIEW_HARD_COLUMN_MIN_WIDTH}
          isDisabled={!canEditFields}
          contentId={contentId}
          hasNumberedEntries={hasNumberedEntries}
          fieldIds={databaseDraftView.fields}
          databaseDraft={databaseDraftView}
        >
          {(currentWidths) => (
            <>
              <Table
                ref={tableRef}
                id="layout-table"
                data-testid="layout-table"
                minWidth={minWidth}
                gridTemplateColumns={gridTemplateColumns}
                actionsMidWidth={RIGHT_ACTIONS_MID_WIDTH}
                containerHeight={containerHeight}
                canEditContent={canEditContent}
                hasNumberedEntries={hasNumberedEntries}
                dragHandlerTdElementWidth={dragHandlerTdElementWidth}
                isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
              >
                <thead>
                  <TableLayoutHeaderRow
                    contentId={contentId}
                    canEditContent={canEditContent}
                    canEditFields={canEditFields}
                    couldEditFields={couldEditFields}
                    containerHeight={containerHeight}
                    containerLeft={containerLeft}
                    ref={tableHeaderRowRef}
                    tableBodyRef={tableBodyRef}
                    isConfluencePanelOpen={isConfluencePanelOpen}
                  />
                </thead>
                <TableLayoutRows
                  contentId={contentId}
                  isContentEditable={isContentEditable}
                  canEditFields={canEditFields}
                  canEditContent={canEditContent}
                  editingIds={editingIds}
                  setEditingIds={setEditingIds}
                  renderSection={renderSection}
                  currentWidths={currentWidths}
                  containerWidth={containerWidth}
                  containerTop={containerTop}
                  isJiraFilterLoading={isJiraFilterLoading}
                  ref={tableBodyRef}
                />
              </Table>
            </>
          )}
        </TableResizer>
        <Box
          id="add-entry-quick-calc-container"
          xcss={addEntryAndQuickCalculationsContainerStyles}
          style={{ zIndex: Z_INDEX.ADD_ENTRY_QUICK_CALC_CONTAINER }}
        >
          {canEditContent && (
            <ActionsContainerGutter
              side="left"
              isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
            />
          )}
          <ActionsContainer>
            {canEditContent && (
              <Box xcss={addEntryButtonWrapperStyles}>
                <AddEntryButton
                  addEntryButtonRef={addEntryButtonRef}
                  contentId={contentId}
                  onAdd={handleEntry}
                  width={tableWidth}
                  isFaded={isJiraFilterLoading}
                />
                {isJiraFilterLoading && hasEntries && (
                  <Box
                    xcss={filterLoadingOverlayContainerStyles}
                    style={{ width: tableWidth ?? '100%' }}
                  >
                    <FilterLoadingOverlay />
                  </Box>
                )}
              </Box>
            )}
            <QuickCalculationsRow
              database={databaseDraftView}
              columnWidths={quickCalcColumnWidths}
              containerWidth={containerWidth}
              canEditContent={canEditContent}
              shouldShowEntryPrefix={shouldShowEntryPrefix}
            />
          </ActionsContainer>
          <ActionsContainerGutter side="right" />
        </Box>
      </TableLayoutWrapper>
    </>
  );
};

export default React.memo(TableLayout);
